import { z } from "zod";
import { DECIMAL_ZERO, decimalSchema } from "@shared/values/decimal";

import type { AddressChoices } from "@/lib/address-choices";
import { FORM_VALIDATION_MESSAGES, ORDER_DELIVERY_LABELS } from "@/lib/ui-labels";
import { formatQuantity } from "@shared/values/number-format";
import {
  DELIVERY_ENTRY_FIELDS, DELIVERY_SCHEDULE_FIELD, deliveryEntryPath, deliveryScheduleIssues,
  deliveryScheduleSchema, orderQuantityOf, type DeliveryEntry,
} from "@shared/orders/order-delivery-schedule";
import type { LifecycleDateLabels } from "@shared/orders/order-lifecycle-policy";

export const deliveryRowFormSchema = z.object({
  date: z.string(),
  quantity: z.string(),
  carrier: z.string().default(""),
  batchNumber: z.string().default(""),
  shipToAddressId: z.string().default(""),
});
export type DeliveryRow = z.infer<typeof deliveryRowFormSchema>;
export type DeliveryRowInput = z.input<typeof deliveryRowFormSchema>;

export const EMPTY_DELIVERY_ROW: DeliveryRow = { date: "", quantity: "", carrier: "", batchNumber: "", shipToAddressId: "" };

type FormIssue = { path: (string | number)[]; message: string };

function entryToRow(entry: DeliveryEntry): DeliveryRow {
  return {
    date: entry.date ?? "", quantity: String(entry.quantity), carrier: entry.carrier ?? "", batchNumber: entry.batchNumber ?? "",
    shipToAddressId: entry.shipToAddressId == null ? "" : String(entry.shipToAddressId),
  };
}

function isForeignShipTo(offered: ReadonlySet<string>, row: DeliveryRow): boolean {
  return row.shipToAddressId !== "" && !offered.has(row.shipToAddressId);
}

function withoutShipTo(row: DeliveryRow): DeliveryRow {
  return { ...row, shipToAddressId: "" };
}

export function withoutForeignShipTo(rows: readonly DeliveryRow[], choices: AddressChoices): readonly DeliveryRow[] {
  const foreign = isForeignShipTo.bind(null, new Set(choices.options.map((option) => option.value)));
  if (!choices.ready || !rows.some(foreign)) return rows;
  return rows.map((row) => (foreign(row) ? withoutShipTo(row) : row));
}

export function toDeliveryRows(schedule: readonly DeliveryEntry[]): DeliveryRow[] {
  return schedule.map(entryToRow);
}

function shippingDetails(row: DeliveryRowInput): Partial<DeliveryEntry> {
  const [carrier, batchNumber] = [(row.carrier ?? "").trim(), (row.batchNumber ?? "").trim()];
  const shipTo = Number(row.shipToAddressId);
  return { ...(carrier ? { carrier } : {}), ...(batchNumber ? { batchNumber } : {}), ...(shipTo > 0 ? { shipToAddressId: shipTo } : {}) };
}

function rowToEntry(includeShippingDetails: boolean, row: DeliveryRowInput): DeliveryEntry {
  return { date: row.date || null, quantity: row.quantity, ...(includeShippingDetails ? shippingDetails(row) : {}) };
}

export function toDeliverySchedule(rows: readonly DeliveryRowInput[], includeShippingDetails = true): DeliveryEntry[] {
  return rows.map(rowToEntry.bind(null, includeShippingDetails));
}

export function rowsQuantity(rows: readonly DeliveryRowInput[]): string {
  return orderQuantityOf(rows.map(rowForTotal));
}

function rowForTotal(row: DeliveryRowInput): DeliveryEntry {
  const result = decimalSchema.safeParse(row.quantity);
  return { date: null, quantity: result.success ? result.data : DECIMAL_ZERO };
}

export function deliveryLinesTotalText(rows: readonly DeliveryRowInput[], unit: string): string {
  return ORDER_DELIVERY_LABELS.total(formatQuantity(rowsQuantity(rows)), unit);
}

function formIssue(issue: z.ZodIssue): FormIssue {
  let message = issue.message;
  if (issue.path.at(-1) === DELIVERY_ENTRY_FIELDS.quantity) message = FORM_VALIDATION_MESSAGES.quantityPositive;
  if (issue.path.length === 0 && issue.code === z.ZodIssueCode.too_small) message = FORM_VALIDATION_MESSAGES.deliveryLineRequired;
  return { path: [DELIVERY_SCHEDULE_FIELD, ...issue.path], message };
}

function scheduleIssues(rows: readonly DeliveryRowInput[]): FormIssue[] {
  const result = deliveryScheduleSchema.safeParse(toDeliverySchedule(rows));
  return result.success ? [] : result.error.issues.map(formIssue);
}

function rowDateIssues(row: DeliveryRowInput, index: number, submittedAt: Date | null, labels: LifecycleDateLabels): FormIssue[] {
  if (!row.date) return [];
  return deliveryScheduleIssues(submittedAt, toDeliverySchedule([row]), { ...labels, expectedDeliveryDate: ORDER_DELIVERY_LABELS.date })
    .map(({ message }) => ({ path: deliveryEntryPath(index, DELIVERY_ENTRY_FIELDS.date), message }));
}

export function deliveryScheduleFormIssues(rows: readonly DeliveryRowInput[], submittedAt: Date | null, labels: LifecycleDateLabels = {}): FormIssue[] {
  const issues = scheduleIssues(rows);
  for (const [index, row] of rows.entries()) issues.push(...rowDateIssues(row, index, submittedAt, labels));
  return issues;
}
